/**
 * Platform-level runtime configuration read from platform_settings.
 * SaaS Admin owns these keys; Firm Admin must never write them.
 */
import { dataProvider as supabase } from "@/lib/providers/data-provider";

export type PlatformRuntimeConfig = {
  otpEnabled: boolean;
  documentHostingDefaultDays: number;
  documentHostingMaxDays: number;
  maintenanceMode: boolean;
};

const CACHE_MS = 60_000;
let cached: PlatformRuntimeConfig | null = null;
let cachedAt = 0;

const DEFAULTS: PlatformRuntimeConfig = {
  otpEnabled: false,
  documentHostingDefaultDays: 7,
  documentHostingMaxDays: 7,
  maintenanceMode: false,
};

const KEYS = [
  "auth.otp_enabled",
  "document.hosting.default_ttl_days",
  "document.hosting.max_ttl_days",
  "security.maintenance_mode",
] as const;

function parseBool(value: unknown, fallback: boolean): boolean {
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return fallback;
}

function parseNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function mapRows(rows: Array<{ key: string; value: unknown }>): PlatformRuntimeConfig {
  const m = new Map(rows.map((r) => [r.key, r.value]));
  const maxDays = parseNumber(m.get("document.hosting.max_ttl_days"), DEFAULTS.documentHostingMaxDays);
  const defaultDays = Math.min(
    parseNumber(m.get("document.hosting.default_ttl_days"), DEFAULTS.documentHostingDefaultDays),
    maxDays,
  );
  return {
    otpEnabled: parseBool(m.get("auth.otp_enabled"), DEFAULTS.otpEnabled),
    documentHostingDefaultDays: defaultDays,
    documentHostingMaxDays: maxDays,
    maintenanceMode: parseBool(m.get("security.maintenance_mode"), DEFAULTS.maintenanceMode),
  };
}

export async function fetchPlatformRuntimeConfig(
  opts?: { force?: boolean },
): Promise<PlatformRuntimeConfig> {
  if (!opts?.force && cached && Date.now() - cachedAt < CACHE_MS) {
    return cached;
  }
  try {
    const { data, error } = await supabase
      .from("platform_settings" as never)
      .select("key,value")
      .in("key", [...KEYS]);
    if (!error && data && Array.isArray(data)) {
      cached = mapRows(data as Array<{ key: string; value: unknown }>);
      cachedAt = Date.now();
      return cached;
    }
  } catch {
    // platform_settings may be unavailable on some surfaces
  }
  cached = DEFAULTS;
  cachedAt = Date.now();
  return cached;
}

export async function isOtpLoginEnabled(): Promise<boolean> {
  return (await fetchPlatformRuntimeConfig()).otpEnabled;
}

export async function getDocumentHostingDefaultDays(): Promise<number> {
  return (await fetchPlatformRuntimeConfig()).documentHostingDefaultDays;
}

export async function isMaintenanceModeEnabled(): Promise<boolean> {
  return (await fetchPlatformRuntimeConfig()).maintenanceMode;
}

export function getPlatformRuntimeConfigSync(): PlatformRuntimeConfig {
  return cached ?? DEFAULTS;
}
